import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Brain, Loader2, Save } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { motion, AnimatePresence } from 'framer-motion';
import type { HDType } from '@/lib/types';
import { SYMPTOMS_BY_TYPE } from '@/lib/types';
import { analyzeEnergyCenters } from '@/lib/energyCenters';
import { RecommendationModal } from '@/components/recommendations/RecommendationModal';

interface SymptomAnalysisProps {
  symptoms: string[];
  energyType: HDType;
  onSaveRecommendation?: (recommendation: string) => void;
}

export function SymptomAnalysis({ symptoms, energyType, onSaveRecommendation }: SymptomAnalysisProps) {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const { toast } = useToast();

  // Automatically analyze symptoms when they change
  useEffect(() => {
    if (symptoms.length > 0) {
      analyzeSymptoms();
    } else {
      setAnalysis(null);
    }
  }, [symptoms]);

  // Set up reminder timer when analysis is complete
  useEffect(() => {
    if (analysis && analysis.includes('Recommandations')) {
      const timer = setTimeout(() => {
        setShowModal(true);
      }, 300000); // 5 minutes

      return () => clearTimeout(timer);
    }
  }, [analysis]);

  const analyzeSymptoms = async () => {
    setLoading(true);
    try {
      // Get symptom objects and their impacts
      const symptomObjects = symptoms.map(symptom => {
        // Check if it's a custom symptom
        if (symptom.startsWith('Autre : ')) {
          const customText = symptom.replace('Autre : ', '');
          return {
            text: customText,
            energyImpact: -5, // Default impact for custom symptoms
            isCustom: true
          };
        }
        return SYMPTOMS_BY_TYPE[energyType].find(s => s.text === symptom);
      }).filter((s): s is NonNullable<typeof s> => s !== undefined);

      // Get affected energy centers
      const { affectedCenters, recommendations } = analyzeEnergyCenters(energyType, symptoms);

      // Group symptoms by impact
      const positiveSymptoms = symptomObjects.filter(s => !s.isCustom && s.energyImpact > 0);
      const negativeSymptoms = symptomObjects.filter(s => s.isCustom || s.energyImpact < 0);

      // Generate personalized response
      let response = '';
      
      // Initial analysis
      if (negativeSymptoms.length > 0) {
        response = `Analyse de vos symptômes :\n`;
        response += `Je détecte ${negativeSymptoms.length} point${negativeSymptoms.length > 1 ? 's' : ''} de tension :\n`;
        negativeSymptoms.forEach(s => {
          response += `• ${s.text}\n`;
        });
        
        // Add energy center analysis if applicable
        if (affectedCenters.length > 0) {
          response += `\nCentres énergétiques affectés :\n`;
          affectedCenters.forEach(center => {
            response += `• ${center}\n`;
          });
        }
        
        // Add type-specific recommendations
        response += `\nRecommandations adaptées à votre type ${energyType} :\n`;
        
        if (energyType === 'projector') {
          response += `• Prenez du recul et observez avant d'agir\n`;
          response += `• Attendez les invitations authentiques\n`;
          response += `• Accordez-vous des moments de solitude pour recharger votre énergie\n`;
        } else if (energyType === 'generator' || energyType === 'manifesting-generator') {
          response += `• Écoutez votre réponse sacrale avant d'agir\n`;
          response += `• Engagez-vous dans des activités qui vous procurent de la satisfaction\n`;
          response += `• Faites des pauses actives plutôt que de l'immobilité totale\n`;
        } else if (energyType === 'manifestor') {
          response += `• Informez votre entourage de vos intentions\n`;
          response += `• Prenez le temps de ressentir vos impulsions créatives\n`;
          response += `• Respectez vos cycles d'action et de repos\n`;
        } else if (energyType === 'reflector') {
          response += `• Observez comment l'environnement influence votre énergie\n`;
          response += `• Attendez un cycle lunaire complet avant les décisions importantes\n`;
          response += `• Créez un espace sûr et stable pour vous ressourcer\n`;
        }
        
        // Add practical exercises
        response += `\nExercices pratiques immédiats :\n`;
        recommendations.forEach(rec => {
          response += `• ${rec}\n`;
        });
        
      } else if (positiveSymptoms.length > 0) {
        response = `Félicitations ! Votre énergie est bien alignée.\n\n`;
        response += `Points positifs observés :\n`;
        positiveSymptoms.forEach(s => {
          response += `• ${s.text}\n`;
        });
        
        response += `\nPour maintenir cet alignement :\n`;
        response += `• Notez ce qui fonctionne bien pour vous aujourd'hui\n`;
        response += `• Continuez vos pratiques actuelles\n`;
        response += `• Partagez votre expérience avec d'autres\n`;
        
        if (energyType === 'projector') {
          response += `• Profitez de cette clarté pour guider les autres\n`;
        } else if (energyType === 'generator' || energyType === 'manifesting-generator') {
          response += `• Utilisez cette énergie pour des projets satisfaisants\n`;
        } else if (energyType === 'manifestor') {
          response += `• Initiez de nouveaux projets alignés\n`;
        } else if (energyType === 'reflector') {
          response += `• Notez les conditions environnementales favorables\n`;
        }
      }

      setAnalysis(response);
    } catch (error) {
      console.error('Error analyzing symptoms:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible d'analyser les symptômes.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = () => {
    if (analysis && onSaveRecommendation) {
      onSaveRecommendation(analysis);
      toast({
        title: "Recommandation sauvegardée",
        description: "La recommandation a été ajoutée à vos notes.",
      });
    }
  };

  if (loading) {
    return (
      <Card className="mt-4">
        <CardContent className="p-6 flex items-center justify-center">
          <div className="flex items-center gap-2 text-purple-600">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Analyse en cours...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="mt-4">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Brain className="w-5 h-5 text-purple-600" />
            <h3 className="font-semibold">Analyse IA des symptômes</h3>
          </div>

          <AnimatePresence>
            {analysis && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-4"
              >
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="whitespace-pre-line text-purple-900">
                    {analysis}
                  </div>
                </div>

                <Button
                  onClick={handleSave}
                  variant="outline"
                  className="w-full gap-2"
                >
                  <Save className="w-4 h-4" />
                  Enregistrer cette recommandation
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>

      <RecommendationModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        recommendation={analysis || ''}
        onValidate={() => {
          setShowModal(false);
          if (onSaveRecommendation && analysis) {
            onSaveRecommendation(analysis);
          }
        }}
        onPostpone={() => {
          setShowModal(false);
          // Set a new reminder for 15 minutes later
          setTimeout(() => setShowModal(true), 900000);
          toast({
            title: "Rappel reporté",
            description: "Nous vous rappellerons dans 15 minutes.",
          });
        }}
      />
    </>
  );
}